/**
 * Recent deposits that already have an answer, with a way to change it
 * (Blueprint §6: every classification is a reversible proposal).
 *
 * Without this, an answer that was LEARNED — a sender taught once and applied
 * to every later deposit — would be invisible and unfixable: the deposit would
 * never come back to the queue because it is no longer an open question. So the
 * row says who decided ("learned from this sender" or "you chose this") and
 * offers Change, which is the same five answers as the card.
 *
 * Folded by default (progressive disclosure, A.5): the open questions are the
 * thing that needs the operator; this is the record.
 */

import { createCollapsible } from './collapsible';
import { createAnswerChips, type AnswerHandlers } from './deposit-card';
import {
  depositDateText,
  depositTitle,
  inflowLabel,
  inflowSourceText,
  skimText,
} from '../lib/deposit-copy';
import { formatMoney } from '../lib/envelope-math';
import type { ApiDeposit } from '../lib/api';

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text != null) node.textContent = text;
  return node;
}

function createRow(deposit: ApiDeposit, handlers: AnswerHandlers): HTMLElement {
  const row = el('div', 'deposit-row');
  row.dataset.txnKey = deposit.txnKey;

  const main = el('div', 'deposit-row-main');
  main.append(
    el('span', 'deposit-row-title', depositTitle(deposit)),
    el('span', 'num', formatMoney(deposit.amountMinor)),
  );

  const meta = [
    depositDateText(deposit.date),
    inflowLabel(deposit.inflowType),
    inflowSourceText(deposit),
    skimText(deposit),
  ].filter((part): part is string => part != null);
  const metaLine = el('div', 'soft deposit-row-meta', meta.join(' · '));

  const change = el('button', 'btn-quiet deposit-change', 'Change');
  change.type = 'button';
  change.setAttribute(
    'aria-label',
    `Change the answer for ${formatMoney(deposit.amountMinor)} from ${depositTitle(deposit)}, currently ${inflowLabel(deposit.inflowType)}`,
  );

  // The chips are built only when asked for, and closed again by a second tap.
  let chips: HTMLElement | null = null;
  change.setAttribute('aria-expanded', 'false');
  change.addEventListener('click', () => {
    if (chips) {
      chips.remove();
      chips = null;
      change.setAttribute('aria-expanded', 'false');
      return;
    }
    chips = createAnswerChips(deposit, handlers, deposit.inflowType);
    row.append(chips);
    change.setAttribute('aria-expanded', 'true');
  });

  row.append(main, metaLine, change);
  return row;
}

export interface RecentDepositsOptions extends AnswerHandlers {
  /**
   * Whether the section is open. The app repaints the whole screen after every
   * answer, which would otherwise fold this list shut under someone who is in the
   * middle of changing an answer — so the caller remembers it and passes it back.
   */
  open?: boolean;
  onToggle?: (open: boolean) => void;
}

/** NULL when there is nothing to show — an empty "Recent deposits" would be a dead control. */
export function createRecentDeposits(
  answered: readonly ApiDeposit[],
  opts: RecentDepositsOptions,
): HTMLElement | null {
  if (answered.length === 0) return null;

  const list = el('div', 'deposit-list');
  for (const deposit of answered) list.append(createRow(deposit, opts));

  const section = createCollapsible({
    summary: `Recent deposits · ${answered.length}`,
    body: list,
    open: opts.open,
  });
  section.classList.add('recent-deposits');
  section.addEventListener('toggle', () => opts.onToggle?.(section.open));
  return section;
}
